import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import "material-symbols";
import "../../../../../src/assets/stylesheets/EditorSecondaryNav.scss";
import classNames from "classnames";
import { useMediaQuery } from "react-responsive";
import { MOBILE_MEDIA_QUERY } from "../../../../../src/constants/mediaQueryBreakpoints";
import FocusTrap from "focus-trap-react";
import { getUserRoles } from "../../../../../src/utils/userRoleHelper";
import useSchool from "../../../../../src/hooks/useSchool";
import { hasLoaded } from "../../../../../src/utils/loadingHelpers";
import { Button } from "@raspberrypifoundation/design-system-react";

const EditorSecondaryNav = () => {
  const auth = useAuth();
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const location = useLocation();
  const currentPath = location.pathname.replace(/\/+$/, "");
  const isMobile = useMediaQuery({ query: MOBILE_MEDIA_QUERY });
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const userType = useSelector((state) => state.editor.userType);

  const school = useSelector((state) => state.school);
  const userRoles = getUserRoles(school);
  useSchool({ accessToken: auth.user?.access_token });

  // Only show the secondary nav if the route matches /{locale} or /{locale}/education
  const showSecondaryNav = location.pathname.match(
    /^\/[a-z]{2}(-[A-Z]{2})?(\/education)?\/?$/,
  );

  const toggleMenu = () => setMenuIsOpen(!menuIsOpen);
  const closeMenu = () => setMenuIsOpen(false);

  const homePath = `/${locale}`;
  const educationPath = `/${locale}/education`;

  const homeClasses = classNames("secondary-nav__link", {
    "secondary-nav__link--active": currentPath === homePath,
  });
  const educationClasses = classNames("secondary-nav__link", {
    "secondary-nav__link--active": currentPath === educationPath,
  });

  const linksClasses = classNames("secondary-nav__links", {
    "secondary-nav__links--mobile": isMobile,
  });

  useEffect(() => {
    const appElement = document.querySelector("#app");

    if (isMobile && menuIsOpen && appElement) {
      appElement.style.overflow = "hidden";
    } else if (appElement) {
      appElement.style.overflow = "auto";
    }
  }, [isMobile, menuIsOpen]);

  //  If the escape key is pressed close the menu
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setMenuIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  if (!showSecondaryNav) {
    return;
  }

  return (
    <div className="secondary-nav">
      <span className="secondary-nav__title">
        {t("editorSecondaryNav.codeEditor")}
      </span>
      <FocusTrap active={isMobile && menuIsOpen}>
        <div className={isMobile ? "secondary-nav__links-wrapper--mobile" : ""}>
          {isMobile && (
            <Button
              type="tertiary"
              icon={menuIsOpen ? "close" : "menu"}
              onClick={toggleMenu}
            />
          )}
          {(!isMobile || menuIsOpen) && (
            <div className={linksClasses}>
              <Link
                className={homeClasses}
                to={`/${locale}`}
                onClick={closeMenu}
              >
                <span className="secondary-nav__link-text">
                  {t("editorSecondaryNav.home")}
                </span>
              </Link>
              <Link
                className={educationClasses}
                to={`/${locale}/education`}
                onClick={closeMenu}
              >
                <span className="secondary-nav__link-text">
                  {t("editorSecondaryNav.education")}
                </span>
              </Link>
              {auth.user && hasLoaded(school?.loading) && (
                <>
                  <hr className="secondary-nav__separator" aria-hidden="true" />
                  {userType === "full" && (
                    <Link
                      className="secondary-nav__link secondary-nav__authed-link"
                      to={`/${locale}/projects`}
                      onClick={closeMenu}
                    >
                      <span className="secondary-nav__link-text">
                        {t("editorSecondaryNav.projects")}
                      </span>
                    </Link>
                  )}
                  {!!userRoles.length && (
                    <a
                      className="secondary-nav__link secondary-nav__authed-link"
                      href={`${process.env.REACT_APP_CODE_CLASSROOM_URL}/${locale}/school`}
                      onClick={closeMenu}
                    >
                      <span className="secondary-nav__link-text">
                        {t("editorSecondaryNav.school")}
                      </span>
                    </a>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </FocusTrap>
    </div>
  );
};

export default EditorSecondaryNav;
